import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { AccountPaymentReviewTable, AccountAuthorisationPanel } from 'web-ui-components/lib/finance/accounts';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { Divider } from 'web-ui-components/lib/atoms/layout';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import iftConfirmedContent, { contentPropTypes } from './iftConfirmed.content';
import { hasApiCallInProgress } from '../../../states/api/selectors';
import {
	getIFTConfirmedAwaitingAuthPayments,
	getSomePaymentRatesAreIndicative,
	getIFTFailedPayments,
} from '../../../states/ift/selectors';
import { parseString } from '../../../helpers/contentParser';
import { goToPage, pages, resetApp } from '../../../states/page/actions';
import ConfirmedButtons from '../../../sharedComponents/ConfirmedButtons/ConfirmedButtons';
import {
	isUserPaymentAuthoriser,
	getAuthorisationMessage,
	getSelectedSubmissionDate,
} from '../../../helpers/fieldHelpers';
import { getUserId } from '../../../states/user/selectors';
import ApiConstants from '../../../config/apiConstants';
import { getFirstSubmissionDate } from '../../../states/calendar/selectors';
import { clearCalendar } from '../../../states/calendar/actions';

const mapState = (state) => ({
	hasApiCallInProgress: hasApiCallInProgress(state),
	confirmedPayments: getIFTConfirmedAwaitingAuthPayments(state),
	showIndicativeRateWarning: getSomePaymentRatesAreIndicative(state),
	failedPayments: getIFTFailedPayments(state),
	userId: getUserId(state),
	calendarSubmissionDate: getFirstSubmissionDate(state),
});

const mapDispatch = (dispatch) => ({
	handleBack: () => dispatch(goToPage(pages.IFTPayments)),
	clearCalendar: () => dispatch(clearCalendar()),
	reset: () => dispatch(resetApp),
});

class IFTConfirmedPage extends Component {
	componentWillUnmount() {
		const { clearCalendar } = this.props;
		clearCalendar();
	}
	render() {
		const { content, confirmedPayments, handleRoute, showIndicativeRateWarning, failedPayments, handleReset, userId } =
			this.props;
		const showFailedPayments = !!failedPayments.length;

		const getAuthMessage = ({ authorisers = [], authType, tableData }) => {
			const submissionDate = getSelectedSubmissionDate(tableData);
			const isFutureDatedPayment = moment.isMoment(submissionDate) ? submissionDate.isAfter() : false;

			if (!isUserPaymentAuthoriser(authorisers, this.props.userId) || authorisers.length > 1) {
				return getAuthorisationMessage(authorisers, userId);
			}
			if (authType === ApiConstants.hardToken) return content.hardToken;
			if (authType === ApiConstants.softToken) return content.softToken;
			if (isFutureDatedPayment) {
				return content.authorisationOnSubmissionDate;
			}
			return '';
		};

		const showAuthoriseButton = confirmedPayments.some(
			({ authorisers = [], authType }) => isUserPaymentAuthoriser(authorisers, this.props.userId) && authType === 'HARD'
		);

		return (
			<div>
				<PageHeading isFlush data-test-id="ift-confirmed-page">
					{content.header}
				</PageHeading>
				<p className="u-margin-top-1">{content.subHeader}</p>

				{showFailedPayments && (
					<div>
						<PageHeading data-test-id="ift-failed-list">{content.failedListHeader}</PageHeading>
						<Divider />

						{failedPayments.map((payment) => (
							<AccountPaymentReviewTable.Failed
								tableData={payment.tableData}
								statusContent={payment.statusContent}
								statusTitle={payment.statusTitle}
								key={payment.id}
							/>
						))}
					</div>
				)}

				{!!confirmedPayments.length && showFailedPayments && (
					<div>
						<PageHeading data-test-id="ift-auth-list">{content.authListHeader}</PageHeading>
						<Divider />
					</div>
				)}

				{!!confirmedPayments.length && (
					<div>
						<AccountAuthorisationPanel>
							<div style={{ textAlign: 'center' }}>
								{showAuthoriseButton && (
									<Button display="primary" onClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')}>
										{content.authoriseButtonText}
									</Button>
								)}
							</div>
						</AccountAuthorisationPanel>

						{confirmedPayments.map((payment) => {
							if (!payment.authType && payment.paymentStatus !== ApiConstants.obligate) {
								return <AccountPaymentReviewTable.Default tableData={payment.tableData} />;
							}
							return (
								<AccountPaymentReviewTable.Authorisation
									statusContent={getAuthMessage(payment)}
									statusTitle={payment.statusTitle}
									tableData={payment.tableData}
									key={payment.id}
								/>
							);
						})}
					</div>
				)}
				{showIndicativeRateWarning && (
					<div>
						<p>{parseString(content.importantInfoHeader)}</p>
						<p>{parseString(content.importantInfoText)}</p>
					</div>
				)}

				<ConfirmedButtons
					handleAnotherPaymentClick={handleReset}
					handleGoToAccountsClick={() => handleRoute('WEB-HOMEPAGE')}
					handleCheckPaymentStatusClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')}
					{...content}
				/>
			</div>
		);
	}
}

IFTConfirmedPage.propTypes = {
	content: PropTypes.shape(contentPropTypes),
	handleRoute: PropTypes.func.isRequired,
	confirmedPayments: PropTypes.arrayOf(PropTypes.shape([])),
	failedPayments: PropTypes.arrayOf(PropTypes.shape([])),
	showIndicativeRateWarning: PropTypes.bool,
	reset: PropTypes.func,
	userId: PropTypes.string.isRequired,
	handleReset: PropTypes.func.isRequired,
	calendarSubmissionDate: PropTypes.shape({}).isRequired,
	clearCalendar: PropTypes.func.isRequired,
};

const contentWrapper = withContent({ content: iftConfirmedContent });
export default connect(mapState, mapDispatch)(withScrollTop(contentWrapper(IFTConfirmedPage)));
export const ConnectWrapper = connect(mapState, mapDispatch)(IFTConfirmedPage);
